import { useState } from "react";
import { Pode } from "@/lib/permissoes";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { Button } from "@/components/ui/button";
import { Wallet, Plus, Send, ExternalLink, Handshake } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { Segmented } from "@/components/layout/segmented";
import { StatCard, StatCardGrid } from "@/components/data/stat-card";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { EmptyState } from "@/components/data/empty-state";
import { useAuth } from "@/lib/auth-context";
import { useReceber } from "@/hooks/use-mercado";
import { ProdutorShell } from "@/components/mercado/produtor-shell";
import { StatusChip } from "@/components/mercado/status-chip";
import { ChipFormalizacao } from "@/components/mercado/formalizacao-chip";
import { ChipDinheiro, ChipDivergencia } from "@/components/mercado/dinheiro-chip";
import { StackTable, StackRow, StackCell } from "@/components/mercado/stack-table";
import {
  ListaAgenda, ORDEM_VENCIMENTO, type GrupoAgenda, type BaldeVencimento,
} from "@/components/mercado/lista-agenda";
import { DialogRecebi, type RomaneioParaReceber } from "@/components/mercado/dialog-recebi";
import { SeletorVencimento } from "@/components/mercado/seletor-vencimento";
import { DialogReenviar } from "@/components/mercado/dialog-reenviar";
import { fmtMoedaBR, fmtKgBR, fmtDataCurtaBR, fmtDiaCurto, fmtIntervaloCurto } from "@/lib/formato-mercado";
import type { ItemReceberDTO, ReceberDTO } from "@shared/mercado";

type Visao = "vencimento" | "comprador";

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO_RECEBER = { visao: "vencimento" };
const PERMITIDOS_RECEBER = { visao: ["vencimento", "comprador"] } as const;

/**
 * A receber: romaneios formalizados, por vencimento.
 *
 * ⚠️ QUEM REGISTRA QUITA, dos dois lados. O "Recebi" daqui marca a venda como
 * paga no Cacho pelo caminho que o Cacho já usa, e o pagamento que o comprador
 * registra chega pronto — não há "Confirmar / Contestar" nesta tela desde que a
 * regra 5 saiu. Totais vêm do servidor; aqui só se formata.
 */
export default function MercadoReceber() {
  const { user } = useAuth();
  const { estado, definir } = useEstadoNaUrl(PADRAO_RECEBER, PERMITIDOS_RECEBER);
  const visao = estado.visao as Visao;
  const [lancar, setLancar] = useState(false);
  const consulta = useReceber(user?.orgTipo !== "COMPRADOR");
  const { data } = consulta;
  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });

  const abertos: RomaneioParaReceber[] = data
    ? [...data.grupos.vencidas.itens, ...data.grupos.semana.itens, ...data.grupos.proximas.itens, ...data.grupos.semVencimento.itens]
      // `aReceber` (base − pago), não `valor`: é o que o lançamento quita.
      .map(i => ({ vendaId: i.vendaId, numero: i.numero, comprador: i.comprador, valor: i.aReceber, dataVenda: i.data }))
    : [];

  return (
    <ProdutorShell>
      <PageHeader
        titulo="A receber"
        descricao="Tudo que você tem para receber, por vencimento. Bate com o Cacho."
        empilharQuandoEstreito
        acoesSecundarias={
          <Segmented<Visao>
            valor={visao}
            onChange={v => definir({ visao: v })}
            testId="seg-visao-receber"
            // A largura quem decide é `.acoes-header` (container query), não uma
            // classe de viewport: em janela estreita com sidebar as duas regras
            // discordavam e o segmentado ficava meio-a-meio.
            className="[&>button]:flex-1"
            opcoes={[
              { id: "vencimento", label: "Por vencimento" },
              { id: "comprador", label: "Por comprador" },
            ]}
          />
        }
        acao={
          <Button onClick={() => setLancar(true)} disabled={abertos.length === 0} data-testid="button-lancar-recebimento">
            <Plus className="h-4 w-4 mr-1" />Lançar recebimento
          </Button>
        }
      />

      {aguardando || !data ? (
        aguardando
      ) : (
        <>
          {/* Cada tile diz de QUANTOS o número é feito. Um valor em reais sozinho
              não se lê: "R$ 62.750,00" pode ser um romaneio ou vinte, e é o que o
              produtor precisa saber para decidir se olha a lista. As contagens
              vêm do servidor (`kpis`), não recontadas aqui. */}
          <StatCardGrid>
            <StatCard
              rotulo="Em aberto"
              valor={fmtMoedaBR(data.kpis.emAberto)}
              testId="tile-em-aberto"
              legenda={`${plural(data.kpis.emAbertoRomaneios, "romaneio", "romaneios")} · ${plural(data.kpis.emAbertoCompradores, "comprador", "compradores")}`}
            />
            <StatCard
              rotulo="Vence esta semana"
              valor={fmtMoedaBR(data.kpis.venceSemana)}
              testId="tile-vence-semana"
              legenda={
                data.grupos.semana.itens.length > 0
                  ? <StatusChip kind="warn">{plural(data.grupos.semana.itens.length, "romaneio", "romaneios")}</StatusChip>
                  : `até ${fmtDiaCurto(data.janelaSemana.fim)}`
              }
            />
            <StatCard
              rotulo="Vencido"
              valor={fmtMoedaBR(data.kpis.vencido)}
              testId="tile-vencido"
              className={data.kpis.vencido === 0 ? "text-muted-foreground" : undefined}
              legenda={
                data.grupos.vencidas.itens.length > 0
                  ? <StatusChip kind="danger">{plural(data.grupos.vencidas.itens.length, "romaneio", "romaneios")}</StatusChip>
                  : "nada atrasado"
              }
            />
            <StatCard
              rotulo="Recebido no mês"
              valor={fmtMoedaBR(data.kpis.recebidoMes)}
              testId="tile-recebido-mes"
              legenda={`${plural(data.kpis.recebidoMesQtd, "romaneio pago", "romaneios pagos")}`}
            />
          </StatCardGrid>

          {abertos.length === 0 ? (
            <EmptyState
              icone={Wallet}
              titulo="Nada a receber"
              descricao="Romaneios formalizados em aberto aparecem aqui, por vencimento. Formalize uma venda emitida no Cacho."
            />
          ) : visao === "vencimento" ? (
            <PorVencimento data={data} />
          ) : (
            <PorCorretor data={data} />
          )}
        </>
      )}

      {lancar && <DialogRecebi romaneios={abertos} onFechar={() => setLancar(false)} />}
    </ProdutorShell>
  );
}

/** "1 romaneio" / "3 romaneios" — o número junto do que ele conta. */
function plural(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/** O número do romaneio leva à venda no Cacho, nas duas visões. */
function LinkVenda({ numero, vendaId }: { numero: string; vendaId: string }) {
  // `abrir`, não `setLocation`: grava esta URL como origem, e o "Voltar" do
  // detalhe da venda devolve para A receber em vez de cair em /cacho/vendas.
  const { abrir } = useNavegacao();
  return (
    <button
      type="button"
      className="font-semibold inline-flex items-center gap-1 underline underline-offset-2 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
      onClick={() => abrir(`/cacho/vendas/${vendaId}`)}
      data-testid={`link-venda-${vendaId}`}
    >
      Nº {numero}<ExternalLink className="h-3 w-3" />
    </button>
  );
}

function PorVencimento({ data }: { data: ReceberDTO }) {
  const { vencidas, semana, proximas, semVencimento } = data.grupos;
  const gVencidas: GrupoAgenda = {
    id: "vencidas", titulo: "Vencidas", tom: "erro", quantidade: vencidas.itens.length,
    totalFormatado: fmtMoedaBR(vencidas.total), vazio: "Nada vencido.",
    children: <TabelaItens itens={vencidas.itens} />,
  };
  // O título diz o INTERVALO que o grupo cobre. Sem isso a tela se contradizia:
  // a linha contava "em 7 d" (dias corridos) e o grupo, que vai só até domingo,
  // anunciava "nada vence nos próximos 7 dias".
  const gSemana: GrupoAgenda = {
    id: "semana",
    titulo: `Esta semana · ${fmtIntervaloCurto(data.janelaSemana.inicio, data.janelaSemana.fim)}`,
    tom: "atencao", quantidade: semana.itens.length,
    totalFormatado: fmtMoedaBR(semana.total),
    vazio: `Nada vence até ${fmtDiaCurto(data.janelaSemana.fim)}.`,
    // A ÚNICA exceção da regra "vazio não renderiza": é a pergunta que a pessoa
    // abriu a tela para fazer, e a resposta "nada" só vale se aparecer.
    mostrarVazio: true,
    children: <TabelaItens itens={semana.itens} />,
  };
  const gProximas: GrupoAgenda = {
    id: "proximas", titulo: "Próximas", tom: "neutro", quantidade: proximas.itens.length,
    totalFormatado: fmtMoedaBR(proximas.total), vazio: "Nenhum vencimento mais adiante.",
    children: <TabelaItens itens={proximas.itens} />,
  };
  // ⚠️ Só DATA no título. Dizia "Sem prazo · falta formalizar" e juntava duas
  // coisas independentes — dá para ter prazo sem formalizar, e o contrário. A
  // formalização continua onde sempre esteve: por linha.
  const gSemVencimento: GrupoAgenda = {
    id: "semVencimento", titulo: "Sem vencimento", tom: "atencao", quantidade: semVencimento.itens.length,
    totalFormatado: fmtMoedaBR(semVencimento.total),
    vazio: "Todo romaneio em aberto tem data.",
    children: <TabelaItens itens={semVencimento.itens} />,
  };
  // A ordem sai de `ORDEM_VENCIMENTO`, compartilhada com as telas do comprador —
  // ver o ⚠️ lá. Vencidas primeiro, sempre; vazio não renderiza.
  const porId: Record<BaldeVencimento, GrupoAgenda> = {
    vencidas: gVencidas, semana: gSemana, proximas: gProximas, semVencimento: gSemVencimento,
  };
  return (
    <ListaAgenda
      grupos={ORDEM_VENCIMENTO.map(id => porId[id])}
      testId="agenda-vencimento"
    />
  );
}

function PorCorretor({ data }: { data: ReceberDTO }) {
  const grupos: GrupoAgenda[] = data.porComprador.map(g => ({
    id: g.compradorId,
    titulo: g.nome,
    // O nome do grupo é a porta do detalhe daquele corretor — a mesma que o card
    // em Corretores abre.
    href: `/mercado/corretores/${g.compradorId}`,
    tom: g.itens.some(i => i.dias !== null && i.dias < 0) ? "erro" : g.itens.some(i => i.dias !== null && i.dias <= 6) ? "atencao" : "neutro",
    quantidade: g.itens.length,
    totalFormatado: fmtMoedaBR(g.total),
    vazio: "",
    children: <TabelaItens itens={g.itens} />,
  }));
  return <ListaAgenda grupos={grupos} testId="agenda-comprador" />;
}

/** Já recebeu o romaneio (pelo link enviado, ou na tela, por ter conta): o WhatsApp é lembrete. */
function ehLembrete(i: ItemReceberDTO): boolean {
  return !!i.enviadoEm || !i.formalizacao.link;
}

function TabelaItens({ itens }: { itens: ItemReceberDTO[] }) {
  const { abrir } = useNavegacao();
  const [recebi, setRecebi] = useState<ItemReceberDTO | null>(null);
  const [lembrar, setLembrar] = useState<ItemReceberDTO | null>(null);
  // ⚠️ NÃO HÁ MAIS "Confirmar / Contestar" aqui. Com "quem registra quita" o
  // pagamento que o comprador registra já chega quitado: o chip diz Pago e não
  // há nada a responder. Quando o dinheiro NÃO caiu, isso se resolve por fora —
  // era a segunda ação de cada PIX, e virava fila dos dois lados.
  return (
    <>
      <StackTable
        // Seis colunas com a data. Abaixo de 700px o número do romaneio começa a
        // quebrar em duas linhas — daí o limiar maior que o padrão de 560.
        larguraMinima={700}
        colunas={[
          { label: "Romaneio", w: true },
          { label: "Data" },
          { label: "Vence" },
          { label: "Valor", num: true },
          { label: "Pagamento" },
          { label: "" },
        ]}
      >
        {/* A chave é a VENDA, não o compartilhamento: nota não formalizada não
            tem compartilhamento, e todas elas dividiriam a chave `null`. */}
        {itens.map(i => (
          <StackRow key={i.vendaId} testId={`receber-${i.vendaId}`}>
            <StackCell w>
              <LinkVenda numero={i.numero} vendaId={i.vendaId} />
              <span className="text-xs text-muted-foreground"> · {i.comprador} · {fmtKgBR(i.kg)}</span>
              {/* Onde a nota está no caminho até o corretor — o MESMO chip de
                  Vendas: termina em "Formalizada", e o link aparece como sublinha
                  só para quem não tem conta no Rizoma. */}
              <span className="block mt-0.5" data-testid={`estado-envio-${i.vendaId}`}>
                <ChipFormalizacao formalizacao={i.formalizacao} compradorNome={i.comprador} testIdSufixo={i.vendaId} />
              </span>
            </StackCell>
            {/* A data da VENDA, ao lado do vencimento. É ela que ordena o grupo
                "sem prazo" — sem a coluna, a lista parecia sem ordem nenhuma. */}
            <StackCell label="Data">{fmtDataCurtaBR(i.data)}</StackCell>
            {/* ⚠️ A coluna INTEIRA é o seletor — com data ou sem. É aqui que os
                quinze romaneios sem prazo ganham data, um toque cada, sem abrir
                romaneio por romaneio. */}
            <StackCell label="Vence">
              <SeletorVencimento
                id={i.vendaId}
                vencimento={i.vencimento}
                dataVenda={i.data}
                formalizado={i.formalizacao.estado !== "NAO_FORMALIZADA"}
                testIdSufixo={i.vendaId}
              />
              {i.vencimento && i.dias !== null && (
                <span className={`block text-[11px] ${i.dias <= 3 ? "text-atencao font-semibold" : "text-muted-foreground"}`}>
                  {i.dias < 0 ? `há ${Math.abs(i.dias)} d` : i.dias === 0 ? "hoje" : `em ${i.dias} d`}
                </span>
              )}
            </StackCell>
            {/* A BASE: o conferido do comprador manda sobre o emitido. Quando
                diferem, os dois ficam ditos na sublinha — a linha não esconde
                que o documento dizia outra coisa. */}
            <StackCell label="Valor" num>
              <span className="font-semibold" data-testid={`valor-${i.vendaId}`}>{fmtMoedaBR(i.valor)}</span>
              {i.valorConferido !== null && (
                <span className="block text-[11px] text-muted-foreground" data-testid={`base-${i.vendaId}`}>
                  emitido {fmtMoedaBR(i.valorEmitido)} · conferido {fmtMoedaBR(i.valorConferido)}
                </span>
              )}
              {i.pago > 0 && i.aReceber > 0 && (
                <span className="block text-[11px] text-muted-foreground" data-testid={`falta-${i.vendaId}`}>
                  falta {fmtMoedaBR(i.aReceber)}
                </span>
              )}
            </StackCell>
            <StackCell label="Pagamento">
              <span className="inline-flex flex-wrap items-center gap-1">
                <ChipDinheiro estado={i.dinheiro} testIdSufixo={i.vendaId} />
                {i.divergencia && (
                  <ChipDivergencia
                    divergencia={i.divergencia}
                    onAbrir={() => abrir(`/cacho/vendas/${i.vendaId}?secao=conferencia`)}
                    testIdSufixo={i.vendaId}
                  />
                )}
              </span>
            </StackCell>
            <StackCell acts>
              {i.formalizacao.estado === "NAO_FORMALIZADA" ? (
                <Button variant="outline" size="sm" onClick={() => abrir(`/cacho/vendas/${i.vendaId}`)} data-testid={`button-formalizar-${i.vendaId}`}>
                  <Handshake className="h-4 w-4 mr-1.5" />Formalizar
                </Button>
              ) : ehLembrete(i) ? (
                // Com conta no Rizoma o romaneio já está na tela dele: não há o
                // que "enviar" — o WhatsApp, se for, é lembrete de pagamento.
                <Button variant="outline" size="sm" onClick={() => setLembrar(i)} data-testid={`button-lembrar-${i.vendaId}`}>
                  Lembrar
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setLembrar(i)} data-testid={`button-enviar-${i.vendaId}`}>
                  <Send className="h-4 w-4 mr-1.5" />Enviar
                </Button>
              )}
              {/* "Recebi" é do produtor que recebeu por fora e quer lançar ele
                  mesmo — e quita igual, pela mesma regra. Sobre um romaneio que o
                  comprador já pagou não sobra saldo, e a linha nem chega aqui. */}
              <Pode cap="mercado.receber"><Button size="sm" onClick={() => setRecebi(i)} data-testid={`button-recebi-${i.vendaId}`}>Recebi</Button></Pode>
            </StackCell>
          </StackRow>
        ))}
      </StackTable>

      {recebi && (
        <DialogRecebi
          romaneios={[{ vendaId: recebi.vendaId, numero: recebi.numero, comprador: recebi.comprador, valor: recebi.aReceber, dataVenda: recebi.data }]}
          inicialId={recebi.vendaId}
          onFechar={() => setRecebi(null)}
        />
      )}
      {lembrar && lembrar.compartilhamentoId && (
        <DialogReenviar
          compartilhamentoId={lembrar.compartilhamentoId}
          titulo={
            ehLembrete(lembrar)
              ? `Lembrar ${lembrar.comprador} do romaneio ${lembrar.numero}`
              : `Enviar o romaneio ${lembrar.numero} para ${lembrar.comprador}`
          }
          descricao={
            !lembrar.formalizacao.link
              ? `O romaneio já está na tela de ${lembrar.comprador} no Rizoma. Isto é só um lembrete, pelo seu WhatsApp.`
              : lembrar.enviadoEm
                ? "Reenvia o romaneio com o link, pelo seu WhatsApp."
                : "É este link que cria o acesso dele. Sai pelo seu WhatsApp."
          }
          primeiroEnvio={!ehLembrete(lembrar)}
          onFechar={() => setLembrar(null)}
        />
      )}
    </>
  );
}
